import { COMMON } from '@constants';
import styled from '@emotion/styled';
import { Button } from '@lib/uikit';
import { lineClamp } from '@lib/uikit/utils/lineClamp';
import { useTranslation } from 'next-i18next';
import { useState, useRef, useEffect } from 'react';

interface TruncatedTextProps {
    text: string;
    maxLines?: number;
    className?: string;
}

const TextContainer = styled.div<{ isExpanded: boolean; maxLines: number }>`
    ${({ isExpanded, maxLines }) => !isExpanded && lineClamp(maxLines)}
    transition: all 0.3s ease;
`;

const SeeMoreButton = styled(Button)`
    margin-top: ${({ theme }) => theme.spacing.xxs};
    padding: 0;
    min-height: auto;
    font-size: inherit;
    text-decoration: underline;
    background: transparent;
    color: ${({ theme }) => theme.palette.primary.main};

    &:hover {
        background: transparent;
        color: ${({ theme }) => theme.palette.primary.dark};
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

export const TruncatedText: React.FC<TruncatedTextProps> = ({
    text,
    maxLines = 3,
    className,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation(COMMON);

    useEffect(() => {
        const checkOverflow = () => {
            if (textRef.current) {
                const element = textRef.current;
                const lineHeight = parseInt(
                    window.getComputedStyle(element).lineHeight
                );
                const maxHeight = lineHeight * maxLines;

                // Temporarily remove line-clamp to check actual height
                element.style.webkitLineClamp = 'unset';
                element.style.display = 'block';
                const actualHeight = element.scrollHeight;
                element.style.webkitLineClamp = maxLines.toString();
                element.style.display = '-webkit-box';

                setShowButton(actualHeight > maxHeight + 1);
            }
        };

        checkOverflow();

        // Re-check on window resize
        const handleResize = () => checkOverflow();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [text, maxLines]);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    if (!text) return null;

    return (
        <Container className={className}>
            <TextContainer
                ref={textRef}
                isExpanded={isExpanded}
                maxLines={maxLines}
            >
                {text}
            </TextContainer>
            {showButton && (
                <SeeMoreButton
                    variant='underline'
                    onClick={toggleExpanded}
                    buttonSize='small'
                >
                    {isExpanded ? t('close') : t('see_more')}
                </SeeMoreButton>
            )}
        </Container>
    );
};
